import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { en, type MessageKey } from './en'

export type Messages = Record<MessageKey, string>
export type Locale = 'en' | 'fr'

const DICTIONARIES: Record<Locale, () => Promise<Messages>> = {
  en: async () => en,
  fr: async () => (await import('./fr')).fr,
}

export type Translate = (key: MessageKey, vars?: Readonly<Record<string, string | number>>) => string

interface I18nApi {
  readonly locale: Locale
  readonly setLocale: (locale: Locale) => void
  readonly t: Translate
}

const I18nContext = createContext<I18nApi | null>(null)

const STORAGE_KEY = 'bp.locale'

export const isLocale = (value: unknown): value is Locale => value === 'en' || value === 'fr'

/** Preference order: an explicit choice the user made, then the browser's
 *  languages, then the app default. */
export function initialLocale(
  supported: readonly string[],
  fallback: string,
  stored: string | null,
  browser: readonly string[],
): Locale {
  const allowed = (v: string | null | undefined): v is Locale =>
    isLocale(v) && supported.includes(v)
  if (allowed(stored)) return stored
  for (const tag of browser) {
    const base = tag.split('-')[0]
    if (allowed(base)) return base
  }
  return allowed(fallback) ? fallback : 'en'
}

/** `{name}` placeholders. Deliberately not a full ICU implementation — the
 *  moment plurals or gendered forms are needed, that is the signal to adopt a
 *  real library rather than grow this one. */
export function interpolate(template: string, vars?: Readonly<Record<string, string | number>>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  )
}

export interface I18nProviderProps {
  readonly initial?: Locale
  readonly children: ReactNode
}

export function I18nProvider({ initial = 'en', children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(initial)
  const [messages, setMessages] = useState<Messages>(en)

  // Loading the dictionary only inside setLocale would mean mounting with
  // initial='fr' renders English until something calls setLocale — which
  // nothing does on a normal boot. The effect covers the initial locale and
  // is a no-op for 'en', whose dictionary is already the initial state.
  useEffect(() => {
    document.documentElement.lang = initial
    if (initial === 'en') return
    let cancelled = false
    void DICTIONARIES[initial]().then((loaded) => {
      if (!cancelled) setMessages(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [initial])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    document.documentElement.lang = next
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Private browsing or a blocked storage partition. A language that does
      // not persist is a far smaller problem than a crash on boot.
    }
    void DICTIONARIES[next]().then(setMessages)
  }, [])

  const t = useCallback<Translate>(
    (key, vars) => interpolate(messages[key] ?? en[key] ?? key, vars),
    [messages],
  )

  const api = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t])
  return <I18nContext.Provider value={api}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nApi {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}

/** Convenience for the common case of only needing the translate function. */
export const useT = (): Translate => useI18n().t

export const readStoredLocale = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export type { MessageKey }
